import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useI18n, SupportedLang } from '../../i18n/I18n';
import { useTheme } from './ThemeProvider';
import { PaymentSettings } from '../../types/payment.types';
import { motion, AnimatePresence } from 'framer-motion';

interface ProfileSettingsMenuProps {
    isOpen: boolean;
    onClose: () => void;
    paymentSettings: PaymentSettings | null;
    userProfile: { pictureUrl?: string; name?: string } | null;
    onLogout: () => void;
}

export default function ProfileSettingsMenu({
    isOpen,
    onClose,
    paymentSettings,
    userProfile,
    onLogout
}: ProfileSettingsMenuProps) {
    const { lang, setLang, t } = useI18n();
    const { theme, toggleTheme } = useTheme();

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    const profileImageSrc = userProfile?.pictureUrl || paymentSettings?.merchantLogoUrl;
    const userName = userProfile?.name || paymentSettings?.merchantName || 'Usuario';
    const userEmail = paymentSettings?.mail || '';

    const handleSupportClick = () => {
        window.location.href = 'mailto:support@sprintcheckout.com';
    };

    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;

    const content = (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex flex-col justify-end lg:justify-center lg:items-center p-0 lg:p-6 pointer-events-auto">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 bg-black/40 backdrop-blur-md"
                        onClick={onClose}
                    />

                    {/* Modal/Sheet Container */}
                    <motion.div
                        initial={{ y: isDesktop ? 20 : "100%", opacity: 0, scale: 0.95 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: isDesktop ? 20 : "100%", opacity: 0, scale: 0.95 }}
                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        drag={isDesktop ? false : "y"}
                        dragDirectionLock
                        dragConstraints={{ top: 0, bottom: 0 }}
                        dragElastic={0.2}
                        onDragEnd={(e, info) => {
                            if (!isDesktop && (info.offset.y > 100 || info.velocity.y > 500)) {
                                onClose();
                            }
                        }}
                        className="relative w-full lg:max-w-md lg:rounded-2xl rounded-t-3xl shadow-2xl flex flex-col"
                        style={{
                            background: 'var(--modal-bg)',
                            border: '1px solid var(--color-surface-border)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Mobile Drag Handle */}
                        <div className="w-full flex justify-center pt-3 pb-1 lg:hidden cursor-grab active:cursor-grabbing">
                            <div className="w-12 h-1.5 rounded-full bg-slate-grey/40" />
                        </div>

                        {/* Desktop Close Button */}
                        <button
                            onClick={onClose}
                            className="hidden lg:flex absolute top-4 right-4 w-8 h-8 rounded-full items-center justify-center text-txt-muted hover:text-txt-heading hover:bg-surface-hover transition-colors border border-surface-border cursor-pointer"
                            style={{ background: 'var(--color-surface)' }}
                        >
                            <span className="material-symbols-outlined text-[20px]">close</span>
                        </button>

                        <div className="px-6 pb-8 pt-4 lg:pt-8 flex flex-col gap-6">

                            {/* User Identity Header */}
                            <div className="flex flex-col items-center justify-center pt-2">
                                <div className="relative mb-3">
                                    <div className="w-[84px] h-[84px] rounded-full overflow-hidden border border-surface-border bg-surface-hover shadow-lg flex items-center justify-center">
                                        {profileImageSrc ? (
                                            <img src={profileImageSrc} alt="Profile" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                        ) : (
                                            <span className="material-symbols-outlined text-[40px] text-txt-muted">person</span>
                                        )}
                                    </div>
                                    {/* Google Badge Overlay (visual only, based on userProfile presence) */}
                                    {userProfile && (
                                        <div className="absolute bottom-0 right-0 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-md border border-gray-200">
                                            <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google" className="w-4 h-4 object-contain" />
                                        </div>
                                    )}
                                </div>
                                <h2 className="text-[22px] font-bold text-txt-heading leading-tight">{userName}</h2>
                                <p className="text-sm text-txt-muted mt-1">{userEmail}</p>
                            </div>

                            {/* Settings Group Container */}
                            <div className="flex flex-col gap-3 mt-2">

                                {/* Language Setting */}
                                <div className="border border-surface-border rounded-2xl p-4 flex items-center justify-between gap-3" style={{ background: 'var(--card-bg)' }}>
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-txt-muted text-[22px]">language</span>
                                        <span className="font-semibold text-[15px] text-txt-heading">{t('profile.language')}</span>
                                    </div>
                                    <div className="flex bg-surface-hover rounded-xl p-1 gap-1">
                                        {([
                                            { value: 'en', label: 'English' },
                                            { value: 'es-AR', label: 'Español (Arg)' }
                                        ] as { value: SupportedLang; label: string }[]).map((option) => (
                                            <button
                                                key={option.value}
                                                onClick={() => setLang(option.value)}
                                                className={`px-3 py-1.5 text-[13px] font-bold rounded-lg transition-all border-none cursor-pointer ${lang === option.value
                                                    ? 'bg-electric-blue text-white shadow-md'
                                                    : 'text-txt-muted hover:text-txt-heading bg-transparent'
                                                    }`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Theme Setting */}
                                <div className="border border-surface-border rounded-2xl p-4 flex items-center justify-between gap-3" style={{ background: 'var(--card-bg)' }}>
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-txt-muted text-[22px]">palette</span>
                                        <span className="font-semibold text-[15px] text-txt-heading">{t('profile.theme')}</span>
                                    </div>
                                    <div className="flex bg-surface-hover rounded-xl p-1 gap-1">
                                        {(['light', 'dark'] as const).map((option) => (
                                            <button
                                                key={option}
                                                onClick={() => { if (theme !== option) toggleTheme(); }}
                                                className={`px-3 py-1.5 min-w-[80px] text-[13px] font-bold rounded-lg transition-all flex items-center justify-center gap-2 border-none cursor-pointer ${theme === option
                                                    ? 'bg-electric-blue text-white shadow-md'
                                                    : 'text-txt-muted hover:text-txt-heading bg-transparent'
                                                    }`}
                                            >
                                                <span className="material-symbols-outlined text-[16px]">
                                                    {option === 'light' ? 'light_mode' : 'dark_mode'}
                                                </span>
                                                <span className="capitalize">{option === 'light' ? 'Light' : 'Dark'}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Support Link */}
                                <button
                                    onClick={handleSupportClick}
                                    className="w-full hover:bg-surface-hover border border-surface-border rounded-2xl p-4 flex justify-between items-center transition-colors group cursor-pointer"
                                    style={{ background: 'var(--card-bg)' }}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-txt-muted group-hover:text-txt-heading transition-colors text-[22px]">support_agent</span>
                                        <span className="font-semibold text-[15px] text-txt-heading">{t('profile.support')}</span>
                                    </div>
                                    <span className="material-symbols-outlined text-txt-muted group-hover:text-txt-heading transition-colors">chevron_right</span>
                                </button>
                            </div>

                            {/* Logout Button */}
                            <button
                                onClick={onLogout}
                                className="w-full mt-2 py-4 rounded-xl font-bold text-red-500 hover:text-white hover:bg-red-500 transition-colors flex justify-center items-center gap-2 cursor-pointer border-none"
                            >
                                {t('profile.logout')}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );

    // Render safely out of internal DOM stacking contexts!
    return ReactDOM.createPortal(content, document.body);
}
